import React from "react";
import { motion } from "framer-motion";
import {
  Search, Microscope, FileText, Activity, MessageSquare, LineChart,
} from "lucide-react";

const STEPS = [
  { icon: Search, t: "Valoración integral", d: "Análisis completo de tu historia clínica y estado actual." },
  { icon: Microscope, t: "Diagnóstico oportuno", d: "Estudios indicados con criterio clínico y precisión." },
  { icon: FileText, t: "Plan personalizado", d: "Estrategia adaptada a tus objetivos y particularidades." },
  { icon: Activity, t: "Seguimiento continuo", d: "Revisiones programadas con bitácora clínica clara." },
  { icon: MessageSquare, t: "Comunicación directa", d: "Canal abierto para tus dudas en cualquier momento." },
  { icon: LineChart, t: "Evaluación de resultados", d: "Medición de avances y ajustes informados." },
];

export default function ModeloAtencionTimeline() {
  return (
    <section
      data-testid="modelo-atencion"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-brand-deep via-brand-medical to-brand-bright" />
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-white/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-brand-bright/30 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-12">
        <div className="flex flex-col items-center text-center mb-14 max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-[0.28em] text-brand-silver mb-3">Nuestro modelo de atención</p>
          <h2 className="font-serif text-4xl md:text-5xl text-white leading-[1.05]">
            Un proceso clínico claro <em className="not-italic text-brand-silver">en seis pasos</em>.
          </h2>
          <p className="mt-5 text-white/75 font-light text-lg leading-relaxed">
            Cada etapa está pensada con la rigurosidad de un hospital privado de alta
            especialidad y la calidez de una consulta cercana.
          </p>
        </div>

        {/* Timeline grid */}
        <div className="relative grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.t}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`modelo-step-${i}`}
              className="group relative rounded-3xl bg-white/10 backdrop-blur-md ring-1 ring-white/15 p-7 hover:bg-white/15 transition-colors duration-500 overflow-hidden"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="w-12 h-12 rounded-2xl bg-white/15 ring-1 ring-white/20 flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-white" strokeWidth={1.4} />
                </div>
                <span className="font-serif text-3xl text-brand-silver/70 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="font-serif text-2xl text-white leading-tight mb-2">{s.t}</h3>
              <p className="text-white/75 font-light leading-relaxed">{s.d}</p>
              <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.div>
          ))}
        </div>

        <p className="mt-10 text-center text-xs uppercase tracking-[0.25em] text-brand-silver">
          Calidad clínica · Seguridad del paciente · Experiencia humana
        </p>
      </div>
    </section>
  );
}
